import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { goBack, push } from 'connected-react-router';
import { isBrowser, isMobile } from 'react-device-detect';
import { ErrorBoundary } from 'react-error-boundary';

import { sessionLogout, sessionStatus, userProfile } from '../../actions/auth';
import { closeModal, openModalAction } from '../../actions/modal';
import { patientAction } from '../../actions/patient';
import LoadingRenderer from '../../components/LoadingRenderer';
import Notification from '../../components/Notification/view/Notification';
import Strings from '../../Strings';
import { authUnset, setLoggingOut } from '../../utils';
import ChatwootWidget from '../../components/ChatwootWidget/ChatwootWidget';
import resetChatwootSession from '../../utils/chatwoot';
import { setTheme, setThemeFromLS } from '../../utils/colorTheme';
import { isEsi } from '../../utils/portalType';
import {
  SESSION_EXPIRY_WARNING_MS,
  SESSION_TIMING_UPDATED_EVENT,
} from '../../utils/sessionTimeout';
import {
  getOrgnization,
  getRoleFromSessionStorage,
  isDoctor,
  isESIPatient,
  isOrganizationManager,
  isPatient,
  PATIENT,
} from '../../utils/userRoles';
import ModalMaster from '../ModalMaster';
import AppHeader from './components/AppHeader';
import { Sidebar } from './components/Sidebar';
import { getLanguage } from '../../utils/language';
import { setBrand, setBrandFromLS } from '../../utils/brand';
import { getDefaultPageForUser } from '../../utils/defaultPages';
import { MobileHeader } from './components/AppHeader/MobileHeader';
import Fallback from './components/Fallback';
import lazyWithRetry from '../../utils/lazyWithRetry';
import { CAP_SET_BY_SMS } from '../../pages/ESI/PatientLogin/redux/constants';

// Keep ConfirmYouThere non-lazy as it's used for session timeout (critical path)
import ConfirmYouThere, { MODAL_CONFIRM_YOU_THERE } from '../../modals/ConfirmYouThere';

// Modal name constants needed for non-lazy imports
import { EditCareProviderApiRouteModalName } from '../../modals/EditCareProviderApiRoute';
import { EditCareProviderApiGeneralSettingsModalName } from '../../modals/EditCareProviderApiGeneralSettings';
import { ROLE_SWITCHER_MODAL_NAME } from '../../modals/RoleSwitcherModal';
import { SAVE_ORG_TEMPLATE_MODAL_NAME } from '../../modals/SaveOrgTemplateModal';
import { EDIT_ORG_TEMPLATE_MODAL_NAME } from '../../modals/EditOrganizationTemplateModal';
import { CREATE_ORG_TEMPLATE_MODAL_NAME } from '../../modals/CreateOrganizationTemplateModal';
import { HEALTH_EVENT_VALUE_MODAL_NAME } from '../../modals/HealthEventValueModal';
import { CREATE_ISSUE_MODAL_NAME } from '../../modals/CreateIssueModal';
import { CUSTOM_WORK_TYPE_MODAL_NAME } from '../../modals/CustomWorkTypeModal';
import CreateRoleModal from '../../modals/CreateRoleModal';
import { EDIT_VITALS_LIMITS_AND_GOALS_MODAL_NAME } from '../../modals/EditVitalsLimitsAndGoalsModal';
import { EDIT_ANNOTATIONS_MODAL_NAME } from '../../modals/EditAnnotationsModal';
import { EDIT_ALERT_RULE_MODAL_NAME } from '../../modals/EditRuleModal';
import { EDIT_SUBSCRIPTION_MODAL_NAME } from '../../modals/EditSubscriptionModal';
import { CHANGE_INVOICES_STATUS_MODAL_NAME } from '../../modals/ChangeInvoicesStatusModal';
import { EDIT_DEVICE_SAMPLE_STATUS_MODAL_NAME } from '../../modals/EditDeviceSampleStatusModal';
import { EDIT_EXTERNAL_PROVIDER_MODAL_NAME } from '../../modals/EditExternalProviderModal';
import { ADD_FILE_MODAL_NAME } from '../../modals/AddFileModal';
import { ASSIGN_DEVICE_INSTRUCTION_VIDEO_MODAL_NAME } from '../../modals/AssignDeviceInstructionVideoModal';

// Lazy load all modals - they are only loaded when opened
const AddEditHardwareId = lazyWithRetry(() => import('../../modals/AddEditHardwareId/AddEditHardwareId'));
const AddEditProvisioning = lazyWithRetry(() => import('../../modals/AddEditProvisioning/AddEditProvisioning'));
const AddESIPatientModal = lazyWithRetry(() => import('../../modals/AddESIPatientModal/AddESIPatientModal'));
const AddNickNameModal = lazyWithRetry(() => import('../../modals/AddNickNameModal'));
const BulkAddPatientsModal = lazyWithRetry(() => import('../../modals/BulkAddPatients/BulkAddPatientsModal'));
const AttachCapToOrgnization = lazyWithRetry(() => import('../../modals/AttachCapToOrgnization/AttachCapToOrgnization'));
const AttachCapToSchedule = lazyWithRetry(() => import('../../modals/AttachCapToSchedule/AttachCapToSchedule'));
const ConfirmationModal = lazyWithRetry(() => import('../../modals/ConfirmationModal'));
const DownloadHistoryModal = lazyWithRetry(() => import('../../modals/DownloadHistoryModal/DownloadHistoryModal'));
const DownloadMedicationModal = lazyWithRetry(() => import('../../modals/DownloadMedicationModal/DownloadMedicationModal'));
const MonthlySummaryModal = lazyWithRetry(() => import('../../modals/MonthlySummaryModal'));
const EditAdminModal = lazyWithRetry(() => import('../../modals/EditAdminModal'));
const EditCaregiverModalOld = lazyWithRetry(() => import('../../modals/EditCaregiverModal'));
const EditOrganization = lazyWithRetry(() => import('../../modals/EditOrganization'));
const EditPatientModal = lazyWithRetry(() => import('../../modals/EditPatientModal'));
const EditPatientScheduleModal = lazyWithRetry(() => import('../../modals/EditPatientScheduleModal/EditPatientScheduleModal'));
const EditProfileModal = lazyWithRetry(() => import('../../modals/EditProfileModal'));
const EditStockModal = lazyWithRetry(() => import('../../modals/EditStockModal/EditStockModal'));
const EditStudyModal = lazyWithRetry(() => import('../../modals/EditStudyModal'));
const EditExternalProviderModal = lazyWithRetry(() => import('../../modals/EditExternalProviderModal'));
const EmptyModal = lazyWithRetry(() => import('../../modals/EmptyModal/EmptyModal'));
const ESIPatientEditAddressModal = lazyWithRetry(() => import('../../modals/ESIPatientEditAddressModal/ESIPatientEditAddressModal'));
const ESIPatientEditDetailsModal = lazyWithRetry(() => import('../../modals/ESIPatientEditDetailsModal/ESIPatientEditDetailsModal'));
const TimezoneChangeModal = lazyWithRetry(() => import('../../modals/TimezoneChangeModal/TimezoneChangeModal'));
const ESIPatientUpdateHardwareCapIdModal = lazyWithRetry(() => import('../../modals/ESIPatientUpdateHardwareCapIdModal/ESIPatientUpdateHardwareCapIdModal'));
const InvitePatientModal = lazyWithRetry(() => import('../../modals/InvitePatientModal'));
const NewConfirmationModal = lazyWithRetry(() => import('../../modals/NewConfirmationModal/NewConfirmationModal'));
const NewTrialPatientModal = lazyWithRetry(() => import('../../modals/NewTrialPatientModal'));
const OnBoardingTutorialModal = lazyWithRetry(() => import('../../modals/OnBoardingTutorialModal/OnBoardingTutorialModal'));
const SaveOrgTemplateModal = lazyWithRetry(() => import('../../modals/SaveOrgTemplateModal'));
const EditOrganizationTemplateModal = lazyWithRetry(() => import('../../modals/EditOrganizationTemplateModal'));
const CreateOrganizationTemplateModal = lazyWithRetry(() => import('../../modals/CreateOrganizationTemplateModal'));
const HealthEventValueModal = lazyWithRetry(() => import('../../modals/HealthEventValueModal'));
const CreateIssueModal = lazyWithRetry(() => import('../../modals/CreateIssueModal'));
const UpdateTask = lazyWithRetry(() => import('../../modals/UpdateTask/UpdateTask'));
const CompleteMonitoringModal = lazyWithRetry(() => import('../../modals/CompleteMonitoringModal/CompleteMonitoringModal'));
const SendSecurityCodeModal = lazyWithRetry(() => import('../../modals/SendSecurityCodeModal'));
const DateRangeSelectionModal = lazyWithRetry(() => import('../../modals/DateRangeSelectionModal'));
const DateRangeSelectionForEventsReportsModal = lazyWithRetry(() => import('../../modals/DateRangeSelectionForEventsReportsModal'));
const ActivateYourDeviceModal = lazyWithRetry(() => import('../../modals/ActivateYourDeviceModal/ActivateYourDeviceModal'));
const CreateKitAndAddDevices = lazyWithRetry(() => import('../../modals/CreateKitAndAddDevices/CreateKitAndAddDevices'));
const AttachKitModal = lazyWithRetry(() => import('../../modals/AttachKit/AttachKitModal'));
const EditCohortModal = lazyWithRetry(() => import('../../modals/EditCohortModal/EditCohortModal'));
const EditConditionsModal = lazyWithRetry(() => import('../../modals/EditConditionsModal/EditConditionsModal'));
const EditFacilitiesModal = lazyWithRetry(() => import('../../modals/EditFacilitiesModal/EditFacilitiesModal'));
const EditRpmScheduleModal = lazyWithRetry(() => import('../../modals/EditRpmSchedule/EditRpmScheduleModal'));
const OnboardingModal = lazyWithRetry(() => import('../../modals/SummaryModals/OnboardingModal'));
const SendMessageModal = lazyWithRetry(() => import('../../modals/SendMessageModal/SendMessageModal'));
const AddEditNoteModal = lazyWithRetry(() => import('../../modals/SendMessageModal/AddEditNoteModal'));
const EditPatientActionModal = lazyWithRetry(() => import('../../modals/EditPatientActionModal'));
const AccessCareUpdatesModal = lazyWithRetry(() => import('../../modals/AccessCareUpdatesModal'));
const CareUpdatePdfPreviewModal = lazyWithRetry(() => import('../../modals/CareUpdatePdfPreviewModal'));
const EditPrefabReplyModal = lazyWithRetry(() => import('../../modals/EditPrefabReplyModal'));
const SetupRpmModal = lazyWithRetry(() => import('../../modals/SummaryModals/SetupRpmModal'));
const EditCaregiversModal = lazyWithRetry(() => import('../../modals/EditCaregiversModal'));
const EulaModal = lazyWithRetry(() => import('../../modals/EulaModal/EulaModal'));
const TimerModal = lazyWithRetry(() => import('../../modals/TimerModal/TimerModal'));
const ExternalApiModal = lazyWithRetry(() => import('../../modals/ExternalApiModal'));
const EditUserModal = lazyWithRetry(() => import('../../modals/EditUserModal'));
const WidgetConfigurationModal = lazyWithRetry(() => import('../../modals/WidgetConfigurationModal/WidgetConfigurationModal'));
const DashboardConfigurationModal = lazyWithRetry(() => import('../../modals/WidgetConfigurationModal/DashboardConfigurationModal'));
const ChangeStateModal = lazyWithRetry(() => import('../../modals/ChangeStateModal'));
const StartEnrollmentAndSchedulesModal = lazyWithRetry(() => import('../../modals/StartEnrollmentAndSchedulesModal'));
const ResetEnrollmentAndUnattachDevicesModal = lazyWithRetry(() => import('../../modals/ResetEnrollmentAndUnattachDevicesModal'));
const OnboardPatientWizard = lazyWithRetry(() => import('../../modals/OnboardPatientWizard/OnboardPatientWizard'));
const ReassignCareProviderModal = lazyWithRetry(() => import('../../modals/ReassignCareProviderModal'));
const PatientRawDataModal = lazyWithRetry(() => import('../../modals/PatientRawDataModal/PatientRawDataModal'));
const EditOrgGroupModal = lazyWithRetry(() => import('../../modals/EditOrgGroupModal'));
const AddUserToOrgGroup = lazyWithRetry(() => import('../../modals/AddUserToOrgGroup'));
const EditCareProviderApiRoute = lazyWithRetry(() => import('../../modals/EditCareProviderApiRoute'));
const EditCareProviderApiGeneralSettings = lazyWithRetry(() => import('../../modals/EditCareProviderApiGeneralSettings'));
const EditShipmentModal = lazyWithRetry(() => import('../../modals/EditShipmentModal'));
const ShipmentDetailsModal = lazyWithRetry(() => import('../../modals/ShipmentDetailsModal'));
const PrepareShipmentsModal = lazyWithRetry(() => import('../../modals/PrepareShipmentsModal'));
const EditDefaultRemindersModal = lazyWithRetry(() => import('../../modals/EditDefaultRemindersModal/EditDefaultRemindersModal'));
const AddFileModal = lazyWithRetry(() => import('../../modals/AddFileModal'));
const AssignDeviceInstructionVideoModal = lazyWithRetry(() => import('../../modals/AssignDeviceInstructionVideoModal'));
const EditReadingScheduleModal = lazyWithRetry(() => import('../../modals/EditReadingScheduleModal'));
const EditMandatoryTasksModal = lazyWithRetry(() => import('../../modals/EditMandatoryTasksModal'));
const CustomWorkTypeModal = lazyWithRetry(() => import('../../modals/CustomWorkTypeModal'));
const SendSurveyModal = lazyWithRetry(() => import('../../modals/SendSurveyModal'));
const SurveyResponseModal = lazyWithRetry(() => import('../../modals/SurveyResponseModal'));
const ChartReviewModal = lazyWithRetry(() => import('../../modals/ChartReviewModal'));
const UnregisterPatient = lazyWithRetry(() => import('../../modals/UnregisterPatient'));
const CheckEligibilityModal = lazyWithRetry(() => import('../../modals/CheckEligibilityModal'));
const RoleSwitcherModal = lazyWithRetry(() => import('../../modals/RoleSwitcherModal'));
const MoveToNotEnrolledModal = lazyWithRetry(() => import('../../modals/MoveToNotEnrolledModal'));
const CancelEnrollmentModal = lazyWithRetry(() => import('../../modals/CancelEnrollmentModal'));
const EditVitalsLimitsAndGoalsModal = lazyWithRetry(() => import('../../modals/EditVitalsLimitsAndGoalsModal'));
const CompositeGraphConfigurationModal = lazyWithRetry(() => import('../../modals/CompositeGraphConfigurationModal'));
const EditAnnotationsModal = lazyWithRetry(() => import('../../modals/EditAnnotationsModal'));
const EditMedication = lazyWithRetry(() => import('../../modals/OnboardPatientWizard/EditMedication'));
const EditRuleModal = lazyWithRetry(() => import('../../modals/EditRuleModal'));
const EditSubscriptionModal = lazyWithRetry(() => import('../../modals/EditSubscriptionModal'));
const ChangeInvoicesStatusModal = lazyWithRetry(() => import('../../modals/ChangeInvoicesStatusModal'));
const EditDeviceSampleStatusModal = lazyWithRetry(() => import('../../modals/EditDeviceSampleStatusModal'));
const SmartMeterOrderModal = lazyWithRetry(() => import('../../modals/SmartMeterOrderModal'));
const ManualVitalsModal = lazyWithRetry(() => import('../../modals/ManualVitalsModal/ManualVitalsModal'));
const AthenaSearchResultModal = lazyWithRetry(() => import('../../modals/OnboardPatientWizard/AthenaSearchResult'));

class App extends Component {
  hasShownOrg2FASetupPrompt = false;

  sessionPollTimeout = null;

  sessionStatusRequestInFlight = false;

  handleSessionTimingUpdated = event => {
    const { authenticated, closeSessionTimeoutWarning, confirmYouThere } = this.props;
    if (!authenticated) {
      return;
    }

    const expiresAt = Number(event?.detail?.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) {
      return;
    }

    const remainingMs = expiresAt - Date.now();
    const warningThresholdMs = SESSION_EXPIRY_WARNING_MS;

    if (remainingMs > warningThresholdMs) {
      closeSessionTimeoutWarning();
      return;
    }

    if (remainingMs > 0) {
      confirmYouThere(expiresAt);
    }
  };

  constructor(props) {
    super(props);
    this.state = { leftMenuVisible: isBrowser };
  }

  componentDidMount() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      document.body.classList.add('resize-animation-stopper');
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        document.body.classList.remove('resize-animation-stopper');
      }, 400);
    });
    window.addEventListener(SESSION_TIMING_UPDATED_EVENT, this.handleSessionTimingUpdated);

    const {
      authenticated,
      onNotAuthenticated,
      ui_config,
      preferences,
      profile,
      authConfig,
      loadProfile,
      onLogOut,
      location,
    } = this.props;

    if (!authenticated) {
      onNotAuthenticated();
      return;
    }

    if (ui_config?.theme) {
      setTheme(ui_config?.theme);
    } else {
      setThemeFromLS();
    }
    if (ui_config?.brand) {
      setBrand(ui_config?.brand);
    } else {
      setBrandFromLS();
    }

    if (!profile) {
      loadProfile().then(r => {
        const languageCode = getLanguage();
        Strings.setLanguage(languageCode);

        if (isESIPatient()) setTheme('white');

        const config = r.response?.config;
        if (isESIPatient()) {
          // TODO: here we will have a crash when config.esi_is_cap_id_set is not set
          if (!config.esi_is_cap_id_set && !authConfig[CAP_SET_BY_SMS]) {
            onLogOut();
          }
        }

        if (authenticated && r.error && r.response) {
          onLogOut();
          return;
        }
        if (location.pathname === '/') {
          this.showHomePage();
        }
        this.maybeShowOrg2FASetupPrompt();
        this.scheduleSessionTimers();
      });
      return;
    }
    this.maybeShowOrg2FASetupPrompt();
    this.scheduleSessionTimers();
  }

  componentWillUnmount() {
    window.removeEventListener(SESSION_TIMING_UPDATED_EVENT, this.handleSessionTimingUpdated);
    this.clearSessionTimers();
  }

  componentDidUpdate(prevProps) {
    const { eula, openEula, onNotAuthenticated, authenticated, loadProfile, profile, profileRequested } = this.props;

    if (eula !== prevProps.eula && eula?.has_to_be_accepted) {
      openEula(eula);
    }

    if (!authenticated && prevProps.authenticated) {
      this.clearSessionTimers();
      onNotAuthenticated();
      return;
    }

    // This is not used anymore
    if (!profile && !profileRequested) {
      loadProfile();
    }

    if (eula !== prevProps.eula || profile !== prevProps.profile) {
      this.maybeShowOrg2FASetupPrompt();
    }

    if (authenticated && (!prevProps.authenticated || (!prevProps.profile && profile))) {
      this.scheduleSessionTimers();
    }
  }

  shouldShowOrg2FASetupPrompt = () => {
    const { authenticated, eula } = this.props;

    if (!authenticated || eula?.has_to_be_accepted) {
      return false;
    }

    return this.isOrg2FASetupRequired();
  };

  isOrg2FASetupRequired = () => {
    const { profile, role, orgRequires2FASetup, userTwoFAEnabled } = this.props;
    const effectiveRole = (profile?.role || role || '').toLowerCase();
    const setupRecommendedForRole = effectiveRole === 'doctor' || effectiveRole === 'shipper';

    const effectiveOrgRequires2FASetup =
      profile?.org_requires_2fa_setup ??
      orgRequires2FASetup ??
      profile?.org_config?.general?.two_fa_enabled;
    const effectiveUserTwoFAEnabled = profile?.user_two_fa_enabled ?? userTwoFAEnabled;

    // Fail-safe: if backend doesn't provide explicit value, treat as not configured.
    return setupRecommendedForRole && effectiveOrgRequires2FASetup === true && effectiveUserTwoFAEnabled !== true;
  };

  maybeShowOrg2FASetupPrompt = () => {
    const { modalsState, open2FASetupPrompt } = this.props;

    if (this.hasShownOrg2FASetupPrompt || !this.shouldShowOrg2FASetupPrompt()) {
      return;
    }

    const hasOpenModal = Object.values(modalsState || {}).some(modal => modal?.open);
    if (hasOpenModal) {
      return;
    }

    this.hasShownOrg2FASetupPrompt = true;
    open2FASetupPrompt();
  };

  clearSessionTimers = () => {
    clearTimeout(this.sessionPollTimeout);
    this.sessionPollTimeout = null;
    this.sessionStatusRequestInFlight = false;
  };

  openSessionTimeoutWarning = expiresAt => {
    const { confirmYouThere } = this.props;
    confirmYouThere(expiresAt);
  };

  pollSessionStatus = () => {
    if (!this.props.authenticated || this.sessionStatusRequestInFlight) {
      return;
    }

    this.sessionStatusRequestInFlight = true;

    this.props.getSessionStatus().then(response => {
      this.sessionStatusRequestInFlight = false;

      if (!this.props.authenticated) {
        return;
      }

      if (!response || response.error) {
        this.sessionPollTimeout = setTimeout(this.pollSessionStatus, 10000);
        return;
      }

      const remainingSeconds = Number(response.response?.remaining_seconds || 0);
      if (remainingSeconds <= 0) {
        this.props.onSessionExpired();
        return;
      }

      const expiresAt = Date.now() + remainingSeconds * 1000;
      if (remainingSeconds <= SESSION_EXPIRY_WARNING_MS / 1000) {
        this.openSessionTimeoutWarning(expiresAt);
      }

      let nextPollSeconds;
      if (remainingSeconds > SESSION_EXPIRY_WARNING_MS / 1000) {
        // Before warning window, schedule exactly for warning threshold.
        nextPollSeconds = remainingSeconds - SESSION_EXPIRY_WARNING_MS / 1000;
      } else if (remainingSeconds > 10) {
        nextPollSeconds = 10;
      } else {
        nextPollSeconds = 1;
      }

      this.sessionPollTimeout = setTimeout(this.pollSessionStatus, nextPollSeconds * 1000);
    }).catch(() => {
      this.sessionStatusRequestInFlight = false;

      if (!this.props.authenticated) {
        return;
      }

      this.sessionPollTimeout = setTimeout(this.pollSessionStatus, 10000);
    });
  };

  scheduleSessionTimers = () => {
    this.clearSessionTimers();

    if (!this.props.authenticated) {
      return;
    }

    this.pollSessionStatus();
  };

  onPageChange = path => {
    const { onNavigate } = this.props;

    onNavigate(path);
  };

  leftSideMenuToggle = e => {
    this.setState(prevState => ({ leftMenuVisible: !prevState.leftMenuVisible }));
    if (e) {
      e.stopPropagation();
    }
  };

  showHomePage = () => {
    const { onNavigate } = this.props;
    const page = getDefaultPageForUser();
    onNavigate(page);
  };

  render() {
    const {
      children,
      location,
      onLogOut,
      origin,
      userId,
      role,
      notification,
      authenticated,
      profile,
      orgRequires2FASetup,
      userTwoFAEnabled,
    } = this.props;

    if (!authenticated) {
      return <div />;
    }

    if (typeof role === 'undefined') {
      return <LoadingRenderer loading />;
    }

    const { leftMenuVisible } = this.state;

    const isMainPage = isDoctor()
      ? location.pathname.startsWith('/shipper-dashboard')
      : location.pathname.startsWith('/activity');

    if (isEsi()) {
      const favicon = document.getElementById('favicon');
      favicon.href = '../.././faviconLidSync.ico';
    }

    const addReport = isOrganizationManager() && getOrgnization() === 'Centor';
    const restrictFor2FASetup = this.isOrg2FASetupRequired();
    return (
      <div className={`app-root ${isDoctor() ? 'doctor-portal' : 'patient-portal'}`}>
        <div className={`app-main ${isMobile ? 'mobile' : ''}`}>
          {isMobile ? (
            <>
              <MobileHeader
                onMenuClick={this.leftSideMenuToggle}
                onLogoClick={this.showHomePage}
                leftInteractionEnabled={origin !== 'fhir'}
                leftMenuVisible={leftMenuVisible}
              />
              <Sidebar
                leftMenuVisible={leftMenuVisible}
                location={location}
                onChange={path => {
                  this.leftSideMenuToggle();
                  this.onPageChange(path);
                }}
                onLogOut={onLogOut}
                patientId={userId}
                addReport={addReport}
                restrictFor2FASetup={restrictFor2FASetup}
              />
            </>
          ) : (
            <div className={`left-bar ${leftMenuVisible ? '' : 'collapsed'}`}>
              <AppHeader
                {...this.state}
                onLogoClick={this.showHomePage}
                onLeftButtonClick={this.leftSideMenuToggle}
                leftInteractionEnabled={origin !== 'fhir'}
                isMainPage={isMainPage}
                leftMenuVisible={leftMenuVisible}
              />
              {origin === 'fhir' ? null : (
                <Sidebar
                  leftMenuVisible={leftMenuVisible}
                  location={location}
                  onChange={this.onPageChange}
                  onLogOut={onLogOut}
                  patientId={userId}
                  addReport={addReport}
                  restrictFor2FASetup={restrictFor2FASetup}
                />
              )}
            </div>
          )}
          <div className="app-content-container">
            <ErrorBoundary FallbackComponent={Fallback} resetKeys={[location.pathname]}>
              <div className={`app-content ${isMobile ? 'mobile' : ''}`}>{children}</div>
            </ErrorBoundary>
          </div>
        </div>
        <ChatwootWidget />
        {notification.message && <Notification {...notification} />}
        <ModalMaster>
          <EditStudyModal name="new-trial" />
          <EditExternalProviderModal name={EDIT_EXTERNAL_PROVIDER_MODAL_NAME} />
          <ConfirmYouThere name={MODAL_CONFIRM_YOU_THERE} caption={Strings.sessionTimeout} />
          <AddNickNameModal name="add-nick-name" caption={Strings.addNickName} />
          <EditAdminModal name="new-admin" caption={Strings.addAdmin} />
          <EditOrganization name="edit-organization" caption={Strings.editOrganization} />
          <SaveOrgTemplateModal name={SAVE_ORG_TEMPLATE_MODAL_NAME} caption={Strings.saveConfigAsTemplateTitle} />
          <EditOrganizationTemplateModal name={EDIT_ORG_TEMPLATE_MODAL_NAME} caption={Strings.editTemplate} />
          <CreateOrganizationTemplateModal name={CREATE_ORG_TEMPLATE_MODAL_NAME} caption={Strings.createTemplate} />
          <HealthEventValueModal name={HEALTH_EVENT_VALUE_MODAL_NAME} />
          <CreateIssueModal name={CREATE_ISSUE_MODAL_NAME} caption={Strings.superuserHealthCreateIssueTitle} />
          <OnBoardingTutorialModal name="on-boarding-tutorial" caption={Strings.tutorial.dashTitle} />
          <EditPatientModal name="new-patient" caption={Strings.addPatients} />
          <AddESIPatientModal name="add-esi-patient" caption={Strings.addPatients} />
          <EditUserModal name="edit-user" caption={Strings.addAdmin} />
          <EditCareProviderApiRoute name={EditCareProviderApiRouteModalName} />
          <EditCareProviderApiGeneralSettings name={EditCareProviderApiGeneralSettingsModalName} />
          <InvitePatientModal name="invite-patient" caption={Strings.addPatients} />
          <EditCaregiverModalOld name="add-caregiver" caption={Strings.addCaregiver} />
          <EditCaregiversModal name="edit-caregivers" />
          <EditProfileModal name="edit-profile" caption={Strings.editProfile} />
          <ConfirmationModal name="confirmation-modal" />
          <NewTrialPatientModal name="new-trial-patient" caption={Strings.addPatient} />
          <AttachCapToOrgnization name="attach-cap-to-org" caption={Strings.attachCasenp} />
          <SendSecurityCodeModal name="send-security-code" caption={Strings.SendDeviceSecurityCode} />
          <DateRangeSelectionModal name="data-range-selection-modalion" caption="Custom Date Range" />
          <DateRangeSelectionForEventsReportsModal name="date-range-selection-for-events-reports-modal" />
          <AttachCapToSchedule name="attach-cap-to-schedule" />
          <AddEditProvisioning name="add-edit-provisioning" caption={Strings.provisioning} />
          <AddEditHardwareId name="add-edit-hardware-id" caption={Strings.AddEditHardware} />
          <UpdateTask name="update-task" caption={Strings.capPatient.updateTask} />
          <CompleteMonitoringModal name="complete-monitoring-modal" />
          <ESIPatientEditAddressModal name="esi-patient-edit-address-modal" caption={Strings.editAddress} />
          <ESIPatientEditDetailsModal name="esi-patient-edit-details-modal" caption={Strings.editDetails} />
          <NewConfirmationModal name="new-confirmation-modal" caption={Strings.areYouSure} />
          <EditPatientScheduleModal name="edit-patient-schedule" caption={Strings.editSchedule} />
          <BulkAddPatientsModal name="bulk-add-patients" caption={Strings.addPatients} />
          <ESIPatientUpdateHardwareCapIdModal
            name="esi-patient-update-hardware-cap-id-modal"
            caption="Register your Smart Cap"
          />
          <ActivateYourDeviceModal name="activate-your-device-modal" />
          <EditStockModal name="edit-stock" caption={Strings.editStock} />
          <DownloadHistoryModal name="download-history" caption={Strings.downloadHistory} />
          <DownloadMedicationModal name="download-medications" caption={Strings.downloadAllMeds} />
          <MonthlySummaryModal name="monthly-summary-modal" />
          <EmptyModal name="empty-modal" />
          <TimezoneChangeModal name="timezone-change-modal" />
          <CreateKitAndAddDevices name="create-kit-and-add-devices" caption={Strings.createKit} />
          <AttachKitModal name="attach-kit" caption={Strings.attachKit} />
          <EditCohortModal name="edit-cohort" />
          <EditConditionsModal name="edit-conditions" />
          <EditFacilitiesModal name="edit-facilities" />
          <EditRpmScheduleModal name="edit-rpm-schedule" />
          <OnboardingModal name="you-are-all-done" />
          <SendMessageModal name="send-message" noOverlay />
          <AddEditNoteModal name="add-note" noOverlay />
          <EditPatientActionModal name="edit-patient-action" noOverlay />
          <AccessCareUpdatesModal name="access-care-updates" />
          <CareUpdatePdfPreviewModal name="care-update-pdf-preview" />
          <EditPrefabReplyModal name="edit-prefab-reply" />
          <SetupRpmModal name="done-rpm-setup" />
          <EulaModal name="eula" />
          <TimerModal name="timer" noOverlay />
          <ExternalApiModal name="external-api-modal" />
          <WidgetConfigurationModal name="widget-configuration" type="side-panel" />
          <DashboardConfigurationModal name="dashboard-configuration" type="side-panel" />
          <ChangeStateModal name="change-state" />
          <StartEnrollmentAndSchedulesModal name="start-enrollmend-and-schedules" />
          <ResetEnrollmentAndUnattachDevicesModal name="reset-enrollment-and-unattach-devices" />
          <OnboardPatientWizard name="onboard-patient" />
          <ReassignCareProviderModal name="reassign-care-provider" />
          <PatientRawDataModal name="patient-raw-data" />
          <EditOrgGroupModal name="edit-organization-group" />
          <AddUserToOrgGroup name="add-user-to-organization-group" />
          <PrepareShipmentsModal name="prepare-shipments" />
          <EditShipmentModal name="edit-shipment" />
          <ShipmentDetailsModal name="shipment-details" />
          <EditDefaultRemindersModal name="edit-default-reminders" />
          <AddFileModal name={ADD_FILE_MODAL_NAME} />
          <AssignDeviceInstructionVideoModal name={ASSIGN_DEVICE_INSTRUCTION_VIDEO_MODAL_NAME} />
          <EditReadingScheduleModal name="edit-reading-schedule" />
          <EditMandatoryTasksModal name="edit-mandatory-tasks" />
          <CustomWorkTypeModal name={CUSTOM_WORK_TYPE_MODAL_NAME} />
          <SendSurveyModal name="send-survey-modal" />
          <SurveyResponseModal name="survey-response-modal" />
          <ChartReviewModal name="chart-review" />
          <UnregisterPatient name="unregister-patient" />
          <CheckEligibilityModal name="check-eligibility" />
          <RoleSwitcherModal name={ROLE_SWITCHER_MODAL_NAME} />
          <MoveToNotEnrolledModal name="move-to-not-enrolled" />
          <CancelEnrollmentModal name="cancel-enrollment" />
          <CreateRoleModal name="create-role" />
          <EditVitalsLimitsAndGoalsModal name={EDIT_VITALS_LIMITS_AND_GOALS_MODAL_NAME} />
          <CompositeGraphConfigurationModal name="composite-graph-configuration" type="side-panel" />
          <EditAnnotationsModal name={EDIT_ANNOTATIONS_MODAL_NAME} />
          <EditMedication name="edit-medication" />
          <EditRuleModal name={EDIT_ALERT_RULE_MODAL_NAME} />
          <EditSubscriptionModal name={EDIT_SUBSCRIPTION_MODAL_NAME} />
          <ChangeInvoicesStatusModal name={CHANGE_INVOICES_STATUS_MODAL_NAME} />
          <EditDeviceSampleStatusModal name={EDIT_DEVICE_SAMPLE_STATUS_MODAL_NAME} />
          <SmartMeterOrderModal name="place-smartmeter-order-modal" />
          <ManualVitalsModal name="manual-vitals-modal" />
          <AthenaSearchResultModal name="athena-search-results" />
        </ModalMaster>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const { support } = state;
  let tempUserId = '';
  if (state.auth.profile) {
    if (state.auth.profile.id) {
      tempUserId = String(state.auth.profile.id);
    }
  }
  return {
    support,
    role: state.auth.role,
    readOnlyMode: state.auth.read_only,
    authenticated: state.auth.authenticated,
    profile: state.auth.profile,
    userId: tempUserId,
    profileRequested: state.auth.profileRequested,
    path: '',
    trials: state.entities.trials.trial,
    patients: state.entities.patients.patient,
    origin: state.auth.origin,
    modalsState: state.modals || {},
    notification: state.notification.notification || {},
    ui_config: state.auth.ui_config,
    authConfig: state.auth.config,
    preferences: state.auth.preferences,
    eula: state.auth.eula,
    orgConfig: state.auth.org_config,
    orgRequires2FASetup: state.auth.org_requires_2fa_setup,
    userTwoFAEnabled: state.auth.user_two_fa_enabled,
  };
};

const mapDispatchToProps = dispatch => ({
  onNotAuthenticated: () => {
    if (window.location.pathname && window.location.pathname !== '/') {
      localStorage.setItem('redirectUrl', encodeURIComponent(window.location.href));
    }
    dispatch(push('/login'));
  },
  onNavigate: path => dispatch(push(path)),
  onBack: () => dispatch(goBack()),
  confirmYouThere: expiresAt => dispatch(openModalAction(MODAL_CONFIRM_YOU_THERE, { expiresAt })),
  closeSessionTimeoutWarning: () => dispatch(closeModal(MODAL_CONFIRM_YOU_THERE)),
  onLogOut: () => {
    // try to logout to be sure that session is flushed
    setLoggingOut(true);
    dispatch(sessionLogout()).then(() => {
      const isUserPatient = isPatient() || getRoleFromSessionStorage() === PATIENT;
      const isUserEsi = isEsi();
      const location = isUserPatient && isUserEsi ? '/login-esi-patient' : '/login';
      resetChatwootSession();
      authUnset();
      window.location = location;
    });
  },
  onSessionExpired: () => {
    const isUserPatient = isPatient() || getRoleFromSessionStorage() === PATIENT;
    const isUserEsi = isEsi();
    resetChatwootSession();
    authUnset();
    window.location = isUserPatient && isUserEsi ? '/session-expired?esi=1' : '/session-expired';
  },
  loadProfile: () => dispatch(userProfile()),
  getSessionStatus: () => dispatch(sessionStatus()),
  loadPatients: () => dispatch(patientAction.actionList()),
  openEula: eula => dispatch(openModalAction('eula', eula)),
  open2FASetupPrompt: () =>
    dispatch(
      openModalAction('confirmation-modal', {
        caption: 'Two-factor authentication',
        title:
          'Your organization requires 2FA setup. Go to Settings > Security and configure 2FA now to keep your portal access.',
        hideCaution: true,
        hideYesButton: true,
        noButtonText: 'Go to settings',
        onCancelAction: push('/user-settings/security'),
      }),
    ),
});

App.propTypes = {
  onLeftButtonClick: PropTypes.func,
  onLogoClick: PropTypes.func,
  leftMenuVisible: PropTypes.bool,
  profile: PropTypes.object,
  children: PropTypes.any,
  showHomePage: PropTypes.func,
  isMainPage: PropTypes.bool,
  leftInteractionEnabled: PropTypes.bool,
  visible: PropTypes.bool,
  location: PropTypes.object,
  onChange: PropTypes.func,
  onLogOut: PropTypes.func,
  authenticated: PropTypes.bool.isRequired,
  onNotAuthenticated: PropTypes.func.isRequired,
  loadProfile: PropTypes.func.isRequired,
  getSessionStatus: PropTypes.func.isRequired,
  openEula: PropTypes.func.isRequired,
  ui_config: PropTypes.object,
  authConfig: PropTypes.object,
  eula: PropTypes.object,
  onNavigate: PropTypes.func,
  confirmYouThere: PropTypes.func,
  notification: PropTypes.object,
  modalsState: PropTypes.object,
  userId: PropTypes.string,
  role: PropTypes.string,
  origin: PropTypes.string,
  closeSessionTimeoutWarning: PropTypes.func,
  onSessionExpired: PropTypes.func,
  orgConfig: PropTypes.object,
  orgRequires2FASetup: PropTypes.bool,
  userTwoFAEnabled: PropTypes.bool,
  open2FASetupPrompt: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
